import type { Zusammenfuehrung } from '@projektverwaltung/shared';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * Link auf ein Projekt. Unterstrichen (Review V6, WCAG 1.4.1): im Fließtext darf ein Link nicht
 * nur an der Farbe zu erkennen sein.
 */
function ProjektLink({
  id,
  title,
  children,
}: { id: string; title?: string | undefined; children?: ReactNode }) {
  return (
    <Link
      to={`/projects/${id}`}
      title={title}
      className="underline underline-offset-2"
      style={{ fontFamily: 'var(--f-mono)', color: 'var(--primary)' }}
    >
      {children}
    </Link>
  );
}

/**
 * Banner an der zusammengeführten Quelle (#123, Anforderung C5): wohin, wann, durch wen — und
 * wo der Auftrag HEUTE liegt (kann nach einer Kette A → B → C ein anderes Projekt sein).
 *
 * Review V2: EIN Satz mit eingebettetem Link (`<Trans>`) statt Titel + Satz + angehängtem Link —
 * vorher stand „Zusammengeführt Zusammengeführt in P… … Zum Zielprojekt: P…".
 */
export function ZusammenfuehrungBanner({
  zusammenfuehrung: z,
}: { zusammenfuehrung: Zusammenfuehrung }) {
  const { t, i18n } = useTranslation();
  if (!z.in) return null;
  const am = z.am
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(z.am))
    : '—';
  // V4: Ohne auflösbare P-Nummer bleibt der Banner stehen; der Link heißt dann „Zielprojekt".
  const ziel = z.in.project_key ?? t('v2.zusammenfuehren.banner_ziel_ohne_key', 'Zielprojekt');
  const aktuell = z.aktuell_in && z.aktuell_in.project_id !== z.in.project_id ? z.aktuell_in : null;
  return (
    <div
      data-testid="zusammenfuehrung-banner"
      className="rounded-md p-3 text-sm"
      style={{ background: 'var(--warn-soft)', color: 'var(--ink)' }}
    >
      <p>
        <Trans
          i18nKey="v2.zusammenfuehren.banner_satz"
          defaults="<b>Zusammengeführt</b> in <ziel>{{ziel}}</ziel> am {{am}} durch {{von}}."
          values={{ ziel, am, von: z.von_name ?? '—' }}
          components={{ b: <strong />, ziel: <ProjektLink id={z.in.project_id} /> }}
        />
        {aktuell && (
          <>
            {' '}
            <Trans
              i18nKey="v2.zusammenfuehren.banner_aktuell_satz"
              defaults="Der Auftrag liegt aktuell in <aktuell>{{aktuell}}</aktuell>."
              values={{ aktuell: aktuell.project_key }}
              components={{ aktuell: <ProjektLink id={aktuell.project_id} /> }}
            />
          </>
        )}
      </p>
      <p className="text-xs mt-1" style={{ color: 'var(--ink-3)' }}>
        {t(
          'v2.zusammenfuehren.banner_nur_lesbar',
          'Dieses Projekt ist nur noch lesbar. Kommentare, Staffing, Briefings, Befunde und Positionen liegen jetzt im Zielprojekt.',
        )}
      </p>
    </div>
  );
}

/**
 * Hinweis im Ziel (#123, Anforderung C3): aus welchen Projekten hierher zusammengeführt wurde.
 * Jedes davon lässt sich nur noch ansehen.
 */
export function ZusammengefuehrtAusHinweis({ aus }: { aus: Zusammenfuehrung['aus'] }) {
  const { t } = useTranslation();
  if (aus.length === 0) return null;
  const ansehen = t('v2.zusammenfuehren.hinweis_ansehen', 'ansehen (nur lesen)');
  return (
    <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
      {t('v2.zusammenfuehren.hinweis_aus', 'Zusammengeführt aus')}{' '}
      {aus.map((a, i) => (
        <span key={a.project_id}>
          {i > 0 && ', '}
          <ProjektLink id={a.project_id} title={ansehen}>
            {a.project_key}
          </ProjektLink>
        </span>
      ))}{' '}
      → {ansehen}
    </p>
  );
}
